class ShippingEstimator extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('.shipping-estimator__form');
    this.zip = this.querySelector('[name="shipping_address[zip]"]');
    this.button = this.querySelector('.shipping-estimator__button');
    this.status = this.querySelector('.shipping-estimator__status');
    this.abortController = null;
    this.handleCartChanged = this.invalidate.bind(this);

    this.button?.addEventListener('click', this.calculate.bind(this));
    this.zip?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      this.calculate(event);
    });
    document.addEventListener('sail:cart-changed', this.handleCartChanged);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    document.removeEventListener('sail:cart-changed', this.handleCartChanged);
  }

  async calculate(event) {
    event.preventDefault();
    this.clearValidation();

    if (!this.zip.value.trim()) {
      this.zip.classList.add('invalid');
      this.showMessage('Ingresá tu código postal.', 'error');
      return;
    }

    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoading(true);
    let province = '';

    try {
      await this.refreshCartTotal(controller.signal);
      province = this.provinceForZip(this.zip.value);
      if (!province) {
        this.showMessage('Ingresá un código postal argentino válido. También podés usar el CPA alfanumérico.', 'error');
        return;
      }

      const body = new URLSearchParams({
        'shipping_address[zip]': this.zip.value.trim(),
        'shipping_address[province]': province,
        'shipping_address[country]': 'Argentina'
      });
      const prepareResponse = await fetch(this.route('prepare_shipping_rates.json'), {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body,
        signal: controller.signal
      });

      if (!prepareResponse.ok && prepareResponse.status !== 202) throw new Error('Shipping rate preparation failed');
      const rates = await this.pollRates(controller.signal);
      this.renderRates(rates);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        try {
          province ||= this.provinceForZip(this.zip.value);
          const rates = await this.fetchRatesDirect(province, controller.signal);
          this.renderRates(rates);
        } catch (fallbackError) {
          if (fallbackError.name !== 'AbortError') {
            console.error(fallbackError);
            this.showMessage('No pudimos calcular el envío para ese código postal. Vas a poder verlo en el paso de pago.', 'error');
          }
        }
      }
    } finally {
      if (this.abortController === controller && !controller.signal.aborted) this.setLoading(false);
    }
  }

  async pollRates(signal) {
    const deadline = Date.now() + 8000;

    while (Date.now() < deadline) {
      await this.wait(500, signal);
      const response = await fetch(this.route('async_shipping_rates.json'), {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
      } else if (response.status !== 202) {
        const data = await response.json().catch(() => ({}));
        if (response.status === 422) continue;
        throw new Error(data.message || 'Shipping rates request failed');
      }
    }

    throw new Error('Shipping rates request timed out');
  }

  wait(milliseconds, signal) {
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      const timeout = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, milliseconds);
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  renderRates(rates) {
    this.status.replaceChildren();

    if (!rates.length) {
      this.showMessage('No pudimos calcular el envío para ese código postal. Vas a poder verlo en el paso de pago.', 'error');
      return;
    }

    const list = document.createElement('ul');
    list.className = 'shipping-estimator__rates list-unstyled';
    rates.forEach((rate) => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      const price = document.createElement('strong');
      name.textContent = rate.name;
      price.textContent = this.isFree(rate) ? 'Envío gratis' : this.formatPrice(rate.price, rate.currency);
      item.append(name, price);

      const delivery = this.deliveryText(rate);
      if (delivery) {
        const detail = document.createElement('small');
        detail.textContent = delivery;
        item.appendChild(detail);
      }
      list.appendChild(item);
    });
    this.status.appendChild(list);
  }

  isFree(rate) {
    return Number(rate.price) === 0 || Number(this.dataset.cartTotal || 0) >= 15000000;
  }

  async refreshCartTotal(signal) {
    const response = await fetch(this.cartStateRoute(), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal
    });
    if (!response.ok) throw new Error('Cart request failed');
    const cart = await response.json();
    this.dataset.cartTotal = cart.total_price;
  }

  async fetchRatesDirect(province, signal) {
    if (!province) throw new Error('Province could not be inferred');
    const query = new URLSearchParams({
      'shipping_address[zip]': this.zip.value.trim(),
      'shipping_address[province]': province,
      'shipping_address[country]': 'Argentina'
    });
    const response = await fetch(`${this.route('shipping_rates.json')}?${query}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !Array.isArray(data.shipping_rates)) throw new Error(data.message || 'Direct shipping rates request failed');
    return data.shipping_rates;
  }

  formatPrice(price, currency) {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || this.dataset.currency || 'ARS',
      maximumFractionDigits: 2
    }).format(Number(price));
  }

  deliveryText(rate) {
    if (rate.delivery_days?.length === 2) return `Entrega: ${rate.delivery_days[0]} a ${rate.delivery_days[1]} días`;
    if (rate.delivery_range?.length === 2) return `Entrega estimada: ${this.formatDate(rate.delivery_range[0])} a ${this.formatDate(rate.delivery_range[1])}`;
    if (rate.min_delivery_date && rate.max_delivery_date) return `Entrega estimada: ${this.formatDate(rate.min_delivery_date)} a ${this.formatDate(rate.max_delivery_date)}`;
    return '';
  }

  formatDate(value) {
    const date = new Date(`${value}T12:00:00`);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'short' }).format(date);
  }

  provinceForZip(value) {
    const normalized = value.trim().toUpperCase().replace(/\s/g, '');
    const provinceCodes = new Set(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']);
    const cpaProvince = normalized.match(/^([A-Z])\d{4}[A-Z]{0,3}$/)?.[1];
    if (cpaProvince && provinceCodes.has(cpaProvince)) return cpaProvince;

    if (!/^\d{4}$/.test(normalized)) return '';
    const zip = Number(normalized);
    const ranges = [
      [1000, 1499, 'C'], [1600, 1999, 'B'], [2000, 2399, 'S'], [2400, 2499, 'X'],
      [2500, 2699, 'S'], [2700, 2999, 'B'], [3000, 3099, 'S'], [3100, 3299, 'E'],
      [3300, 3399, 'N'], [3400, 3499, 'W'], [3500, 3599, 'H'], [3600, 3699, 'P'],
      [3700, 3799, 'H'], [4000, 4199, 'T'], [4200, 4399, 'G'], [4400, 4499, 'A'],
      [4500, 4699, 'Y'], [4700, 4799, 'K'], [5000, 5299, 'X'], [5300, 5399, 'F'],
      [5400, 5499, 'J'], [5500, 5699, 'M'], [5700, 5799, 'D'], [5800, 5999, 'X'],
      [6000, 6199, 'B'], [6200, 6399, 'L'], [6400, 8199, 'B'], [8200, 8299, 'L'],
      [8300, 8399, 'Q'], [8400, 8999, 'R'], [9000, 9299, 'U'], [9300, 9409, 'Z'],
      [9410, 9499, 'V']
    ];
    return ranges.find(([from, to]) => zip >= from && zip <= to)?.[2] || '';
  }

  invalidate() {
    this.abortController?.abort();
    this.setLoading(false);
    this.showMessage('El carrito cambió. Volvé a calcular el envío.', 'notice');
  }

  route(endpoint) {
    const root = window.routes?.root_url || window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/cart/${endpoint}`;
  }

  cartStateRoute() {
    const root = window.routes?.root_url || window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/cart.js`;
  }

  clearValidation() {
    this.zip.classList.remove('invalid');
  }

  setLoading(loading) {
    this.button.disabled = loading;
    this.button.classList.toggle('loading', loading);
    const idleLabel = this.classList.contains('shipping-estimator--drawer') ? 'Calcular' : 'Calcular envío';
    this.button.textContent = loading ? 'Calculando…' : idleLabel;
    if (loading) this.showMessage('Estamos calculando las opciones de envío…', 'loading');
  }

  showMessage(message, state) {
    this.status.className = `shipping-estimator__status shipping-estimator__status--${state}`;
    this.status.textContent = message;
  }
}

if (!customElements.get('shipping-estimator')) customElements.define('shipping-estimator', ShippingEstimator);
